import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Loader2,
  MessageSquare,
  Radio,
  Sparkles,
  Square,
  Trash2,
  TrendingUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SessionStatusBadge } from '@/components/SessionStatusBadge';
import DanmakuStream from '@/components/DanmakuStream';
import StatsTrendChart from '@/components/StatsTrendChart';
import SentimentAnalysis from '@/components/SentimentAnalysis';
import {
  completeSession,
  deleteSession,
  exportSessionDanmakuToCSV,
  getDanmakuBySession,
  getSessionById,
  getStatsData,
} from '@/lib/api';
import type { Danmaku, LiveSession } from '@/types';
import { formatDuration, formatNumber } from '@/lib/utils';
import { useRealtimeSession } from '@/hooks/useRealtimeSession';

type PendingAction = 'complete' | 'export' | 'delete' | null;

export default function SessionDetailPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<LiveSession | null>(null);
  const [danmakuList, setDanmakuList] = useState<Danmaku[]>([]);
  const [statsData, setStatsData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [error, setError] = useState<string | null>(null);
  const realtimeSession = useRealtimeSession(sessionId || null);

  const loadSessionData = async (id: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const [sessionData, danmakuData, statsResult] = await Promise.all([
        getSessionById(id),
        getDanmakuBySession(id, 1500),
        getStatsData(id),
      ]);
      setSession(sessionData);
      setDanmakuList(danmakuData);
      setStatsData(statsResult);
    } catch (loadError) {
      console.error('加载场次数据失败:', loadError);
      setError('场次数据暂时无法加载');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) void loadSessionData(sessionId);
  }, [sessionId]);

  useEffect(() => {
    if (realtimeSession) setSession(realtimeSession);
  }, [realtimeSession]);

  const handleCompleteSession = async () => {
    if (!sessionId || !window.confirm('确定要完成这个场次吗？场次至少需运行 3 分钟，完成后将停止录制。')) return;
    try {
      setPendingAction('complete');
      await completeSession(sessionId);
      await loadSessionData(sessionId);
    } catch (completeError) {
      console.error('完成场次失败:', completeError);
      window.alert('完成场次失败，请重试');
    } finally {
      setPendingAction(null);
    }
  };

  const handleExport = async () => {
    if (!session) return;
    try {
      setPendingAction('export');
      await exportSessionDanmakuToCSV(session.id, session.room_title || '直播场次');
    } catch (exportError) {
      console.error('导出场次数据失败:', exportError);
      window.alert(exportError instanceof Error ? exportError.message : '导出失败，请重试');
    } finally {
      setPendingAction(null);
    }
  };

  const handleDelete = async () => {
    if (!session || !window.confirm('确定要删除这个场次吗？关联的弹幕数据也会被删除，且无法恢复。')) return;
    try {
      setPendingAction('delete');
      await deleteSession(session.id);
      navigate('/monitor', { replace: true });
    } catch (deleteError) {
      console.error('删除场次失败:', deleteError);
      window.alert('删除场次失败，请重试');
      setPendingAction(null);
    }
  };

  const formatDateTime = (dateString?: string | null) => {
    if (!dateString) return '未设置';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '时间异常';
    return date.toLocaleString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDuration = (current: LiveSession) => {
    if (current.duration_seconds) return current.duration_seconds;
    if (current.status === 'live' && current.actual_start_time) {
      const start = new Date(current.actual_start_time).getTime();
      if (!Number.isNaN(start)) return Math.max(0, Math.floor((Date.now() - start) / 1000));
    }
    return 0;
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto h-7 w-7 animate-spin text-[var(--action-blue)]" />
          <p className="mt-3 text-sm text-[var(--text-muted)]">正在加载场次</p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="console-panel max-w-md px-6 py-10 text-center">
          <Radio className="mx-auto h-8 w-8 text-[var(--border-strong)]" />
          <p className="mt-3 text-base font-semibold text-[var(--text-soft)]">{error || '场次不存在'}</p>
          <p className="mt-1 text-xs leading-5 text-[var(--text-muted)]">返回监控页重新选择一个场次。</p>
          <Button type="button" onClick={() => navigate('/monitor')} className="mt-5">
            <ArrowLeft className="h-4 w-4" />返回直播监控
          </Button>
        </div>
      </div>
    );
  }

  const duration = getDuration(session);

  return (
    <div className="console-page">
      <header className="console-page-header">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
            <button type="button" onClick={() => navigate('/monitor')} className="min-h-11 hover:text-[var(--text-soft)]">直播监控</button>
            <span className="text-[var(--border-control)]">/</span>
            <span className="text-[var(--text-soft)]">场次详情</span>
            <span className="hidden font-mono text-[var(--text-muted)] sm:inline">#{session.id.slice(0, 8).toUpperCase()}</span>
          </div>
          <h1 className="mt-1 truncate text-lg font-bold text-[var(--text-ink)] md:text-xl">{session.room_title || '未命名直播场次'}</h1>
        </div>

        <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap">
          <Button type="button" variant="outline" onClick={() => void handleExport()} disabled={pendingAction !== null}>
            {pendingAction === 'export' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            下载本场数据
          </Button>
          <Button type="button" variant="outline" onClick={() => void handleDelete()} disabled={pendingAction !== null} className="hover:border-[var(--danger-border)] hover:bg-[var(--alert-error-bg)] hover:text-[var(--status-error)]">
            {pendingAction === 'delete' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            删除场次
          </Button>
          {session.status === 'live' && (
            <Button type="button" onClick={() => void handleCompleteSession()} disabled={pendingAction !== null} className="col-span-2 sm:col-span-1">
              {pendingAction === 'complete' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-3.5 w-3.5 fill-current" />}
              {pendingAction === 'complete' ? '正在完成' : '完成录制'}
            </Button>
          )}
        </div>
      </header>

      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_372px]">
        <section className="console-panel">
          <div className="grid md:grid-cols-[minmax(280px,360px)_minmax(0,1fr)]">
            <div className="relative min-h-[190px] overflow-hidden border-b border-[var(--structural-border)] bg-[repeating-linear-gradient(135deg,var(--surface-stripe-a)_0_10px,var(--surface-stripe-b)_10px_20px)] md:min-h-[244px] md:border-b-0 md:border-r">
              <div className="absolute inset-0 flex items-center justify-center"><Radio className="h-12 w-12 text-[var(--border-strong)]" /></div>
              {session.cover_url && (
                <img
                  src={session.cover_url}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                  referrerPolicy="no-referrer"
                  onError={(event) => { event.currentTarget.style.display = 'none'; }}
                />
              )}
              <div className="absolute left-3 top-3 flex gap-1.5">
                <SessionStatusBadge status={session.status} />
                {session.is_recording && <span className="rounded border border-[var(--status-live)] bg-[var(--surface-overlay)] px-2 py-1 text-[11px] font-semibold text-[var(--status-live)]">REC</span>}
              </div>
            </div>

            <div className="flex min-w-0 flex-col p-4 md:p-[18px]">
              <p className="console-kicker">SESSION · 场次概览</p>
              <h2 className="mt-2 text-lg font-bold leading-snug text-[var(--text-ink)] md:text-xl">{session.room_title}</h2>
              <p className="mt-2 text-xs text-[var(--text-muted)]">{session.streamer_name} · 房间号 {session.room_id}</p>
              <dl className="mt-5 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
                <div className="console-metric"><dt className="console-metric-label">已直播</dt><dd className="console-metric-value mt-1 text-base">{duration ? formatDuration(duration) : '—'}</dd></div>
                <div className="console-metric"><dt className="console-metric-label">弹幕数</dt><dd className="console-metric-value mt-1 text-base">{formatNumber(session.danmaku_count)}</dd></div>
                <div className="console-metric"><dt className="console-metric-label">预定开始</dt><dd className="mt-1 font-mono text-xs text-[var(--text-soft)]">{formatDateTime(session.scheduled_start_time)}</dd></div>
                <div className="console-metric"><dt className="console-metric-label">实际开始</dt><dd className="mt-1 font-mono text-xs text-[var(--text-soft)]">{formatDateTime(session.actual_start_time)}</dd></div>
              </dl>
              <p className="mt-auto pt-4 font-mono text-[11px] text-[var(--text-muted)]">实时数据通过场次订阅更新</p>
            </div>
          </div>
        </section>

        <aside className="console-panel p-[18px]">
          <p className="console-kicker">SESSION INFO · 场次信息</p>
          <dl className="mt-4 space-y-3 text-xs">
            <div className="flex justify-between gap-4"><dt className="text-[var(--text-muted)]">状态</dt><dd><SessionStatusBadge status={session.status} /></dd></div>
            <div className="flex justify-between gap-4"><dt className="text-[var(--text-muted)]">录制</dt><dd className={session.is_recording ? 'text-[var(--status-live)]' : 'text-[var(--text-soft)]'}>{session.is_recording ? '录制中' : '未录制'}</dd></div>
            <div className="flex justify-between gap-4"><dt className="text-[var(--text-muted)]">主播</dt><dd className="text-right text-[var(--text-soft)]">{session.streamer_name}</dd></div>
            <div className="flex justify-between gap-4"><dt className="text-[var(--text-muted)]">房间号</dt><dd className="font-mono text-[var(--text-soft)]">{session.room_id}</dd></div>
            <div className="border-t border-[var(--border-subtle)]" />
            <div className="flex justify-between gap-4"><dt className="text-[var(--text-muted)]">预定开始时间</dt><dd className="text-right font-mono text-[var(--text-soft)]">{formatDateTime(session.scheduled_start_time)}</dd></div>
            <div className="flex justify-between gap-4"><dt className="text-[var(--text-muted)]">实际开始时间</dt><dd className="text-right font-mono text-[var(--text-soft)]">{formatDateTime(session.actual_start_time)}</dd></div>
            <div className="flex justify-between gap-4"><dt className="text-[var(--text-muted)]">结束时间</dt><dd className="text-right font-mono text-[var(--text-soft)]">{formatDateTime(session.end_time)}</dd></div>
            <div className="flex justify-between gap-4"><dt className="text-[var(--text-muted)]">弹幕数量</dt><dd className="font-mono text-[var(--text-ink)]">{formatNumber(session.danmaku_count)}</dd></div>
          </dl>
        </aside>
      </div>

      <Tabs defaultValue="stats">
        <div className="overflow-x-auto pb-1">
          <TabsList className="grid min-w-[460px] grid-cols-3">
            <TabsTrigger value="stats" className="gap-2"><TrendingUp className="h-4 w-4" />统计趋势</TabsTrigger>
            <TabsTrigger value="danmaku" className="gap-2"><MessageSquare className="h-4 w-4" />弹幕流</TabsTrigger>
            <TabsTrigger value="sentiment" className="gap-2"><Sparkles className="h-4 w-4" />舆情分析</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="stats" className="mt-4">
          <StatsTrendChart sessionId={session.id} statsData={statsData} />
        </TabsContent>
        <TabsContent value="danmaku" className="mt-4">
          <section className="console-panel p-4 md:p-[18px]">
            <div className="mb-4 flex items-center justify-between border-b border-[var(--border-subtle)] pb-4">
              <div><p className="console-kicker">DANMAKU STREAM</p><h2 className="mt-1 text-sm font-bold text-[var(--text-ink)]">实时弹幕流</h2></div>
              <p className="font-mono text-xs text-[var(--text-muted)]">{danmakuList.length.toLocaleString()} 条已加载</p>
            </div>
            <DanmakuStream sessionId={session.id} initialDanmaku={danmakuList} />
          </section>
        </TabsContent>
        <TabsContent value="sentiment" className="mt-4">
          <SentimentAnalysis session={session} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
